/**
 * Which site this is, and who is signed in.
 *
 * Loaded once at start-up; everything else reads it from context. The site answers
 * "which class's page is this?" (from the host the browser asked for), the session
 * answers "who, and how far through sign-in?".
 */

import { Button, Dialog, Spinner } from '@praxadon/ui';
import {
  type ReactNode,
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ApiError, describeError, onSessionLost, setCsrfToken } from '../api/client.ts';
import { type SessionInfo, type SiteInfo, auth, site as siteApi } from '../api/endpoints.ts';
import { applyBrand } from './branding.ts';

interface SessionContextValue {
  site: SiteInfo;
  session: SessionInfo | null;
  /** Re-read the session from the server, after signing in or out. */
  refresh: () => Promise<SessionInfo | null>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const value = use(SessionContext);
  if (!value) throw new Error('useSession outside SessionProvider');
  return value;
}

/** The display name of the class, or the product for the platform site. */
export function siteName(site: SiteInfo): string {
  return site.kind === 'tenant'
    ? (site.tenant.branding.displayName ?? site.tenant.name)
    : 'Praxadon IDE';
}

async function loadSession(site: SiteInfo): Promise<SessionInfo | null> {
  if (site.kind !== 'tenant') return null;
  const session = await auth.session();
  setCsrfToken(session.authenticated ? session.csrfToken : null);
  return session;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [site, setSite] = useState<SiteInfo | null>(null);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  // The session ended underneath the page (expired, or ended from another device).
  const [lost, setLost] = useState(false);

  useEffect(
    () =>
      onSessionLost(() => {
        setLost(true);
      }),
    [],
  );

  useEffect(() => {
    const unmounted = new AbortController();
    void (async () => {
      try {
        const loadedSite = await siteApi.get();
        const loadedSession = await loadSession(loadedSite);
        if (unmounted.signal.aborted) return;
        setSite(loadedSite);
        setSession(loadedSession);
      } catch (error) {
        if (!unmounted.signal.aborted) setFailure(error);
      }
    })();
    return () => {
      unmounted.abort();
    };
  }, []);

  useEffect(() => {
    if (site?.kind !== 'tenant') return;
    document.title = `${siteName(site)} · Praxadon`;
    applyBrand(
      site.tenant.branding.primaryColor,
      document.documentElement.dataset['theme'] ?? 'dark',
    );
  }, [site]);

  const refresh = useCallback(async () => {
    if (!site) return null;
    const next = await loadSession(site);
    setSession(next);
    return next;
  }, [site]);

  const value = useMemo(() => (site ? { site, session, refresh } : null), [site, session, refresh]);

  if (failure) return <StartupFailure error={failure} />;
  if (!value) {
    return (
      <div className="grid min-h-dvh place-items-center text-fg-muted">
        <Spinner size={24} label="Loading" />
      </div>
    );
  }
  return (
    <SessionContext value={value}>
      {children}
      <SignedOutDialog
        open={lost}
        onSignIn={() => {
          setLost(false);
          // Re-reading the session finds it gone; every signed-in page then sends the
          // student to sign in and brings them back here afterwards.
          void refresh();
        }}
      />
    </SessionContext>
  );
}

/**
 * Told plainly, and only once: unsaved work is not lost — the editor keeps it in this
 * browser and restores it after signing in (docs/adr/0003-editor-documents.md).
 */
function SignedOutDialog({ open, onSignIn }: { open: boolean; onSignIn: () => void }) {
  return (
    <Dialog
      open={open}
      onOpenChange={() => undefined}
      dismissible={false}
      size="sm"
      title="You've been signed out"
      description="For your safety, sign-ins end after a while, or when you sign out on another device."
      footer={
        <Button variant="primary" onClick={onSignIn}>
          Sign in again
        </Button>
      }
    >
      <p className="text-sm text-fg">
        Your unsaved changes are kept in this browser. Sign in again and they will be right where
        you left them.
      </p>
    </Dialog>
  );
}

function StartupFailure({ error }: { error: unknown }) {
  const unknownHost =
    error instanceof ApiError &&
    (error.code === 'host_not_recognised' || error.code === 'tenant_not_found');
  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-fg">
          {unknownHost
            ? "We couldn't find a class at this address"
            : 'Praxadon IDE could not start'}
        </h1>
        <p className="mt-2 text-sm text-fg-muted">
          {unknownHost
            ? 'Check the web address your instructor gave you. It looks like yourclass.ide.praxadon.com.'
            : describeError(error)}
        </p>
        {unknownHost ? null : (
          <button
            type="button"
            onClick={() => {
              window.location.reload();
            }}
            className="mt-4 rounded-lg border border-border px-3 py-1.5 text-sm text-fg hover:bg-surface-hover"
          >
            Try again
          </button>
        )}
      </div>
    </main>
  );
}
